import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
  BreakpointService,
  ModalService,
  normalizeImages,
} from '@boels/helpers';
import { BOProduct, ImageGroup, Images, ImageType } from '@boels/models';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCircle } from '@fortawesome/free-regular-svg-icons';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
} from 'rxjs';
import { CarouselComponent } from '../carousel/carousel.component';
import { ZoomImageComponent } from '../zoom-image/zoom-image.component';
import { MediaComponent } from '../../media/media.component';

@Component({
  selector: 'boels-product-images',
  templateUrl: './product-images.component.html',
  styleUrls: ['./product-images.component.scss'],
  imports: [CommonModule, FontAwesomeModule, CarouselComponent, MediaComponent],
})
export class ProductImagesComponent implements OnInit, OnDestroy {
  protected modalService = inject(ModalService);
  private breakpointService = inject(BreakpointService);

  @Input() componentData: { product$: Observable<BOProduct> };

  protected productData$: Observable<BOProduct>;
  protected mainMediaContainer = new BehaviorSubject<any>(null);
  protected subs$: Subscription = new Subscription();

  thumbs$: Observable<any[]>;
  mainImage$: Observable<any>;

  isTabletAndDown = false;

  faCircle = faCircle;

  ngOnInit(): void {
    this.productData$ = this.updateProduct();

    this.subs$.add(
      this.breakpointService.mdAndBelow$.subscribe(
        (isTabletAndDown: boolean) => (this.isTabletAndDown = isTabletAndDown),
      ),
    );

    this.thumbs$ = this.productData$.pipe(
      map((product: BOProduct) => this.createThumbs(product)),
    );

    this.mainImage$ = combineLatest([
      this.productData$,
      this.mainMediaContainer,
    ]).pipe(map(([, container]) => container));
  }

  ngOnDestroy(): void {
    this.subs$.unsubscribe();
  }

  private updateProduct(): Observable<BOProduct> {
    return this.componentData.product$.pipe(
      switchMap((product: BOProduct) => {
        const images: Images[] = product.variantOptions
          ? product.baseProductImages
          : product.images;

        const updatedImages = normalizeImages(images);

        const updatedProduct = {
          ...product,
          images: updatedImages,
        };

        this.mainMediaContainer.next(
          updatedProduct.images?.[ImageType.PRIMARY]
            ? updatedProduct.images?.[ImageType.PRIMARY]
            : {},
        );
        return of(updatedProduct);
      }),
    );
  }

  showZoomImage(multipleImages = true, main?: any, galleryIndex = 0): void {
    if (!this.isTabletAndDown && (multipleImages || main.zoom)) {
      this.modalService.open(ZoomImageComponent, {
        panelClass: 'modal-image',
        data: {
          thumbs$: this.thumbs$,
          mainImage$: this.mainImage$,
          openOnGalleryIndex: galleryIndex,
        },
      });
    }
  }

  openImage(item: any): void {
    this.mainMediaContainer.next(item);
  }

  isActive(thumbnail: ImageGroup): Observable<boolean> {
    return this.mainMediaContainer.pipe(
      filter(Boolean),
      map((container: any) => {
        return (
          container.zoom &&
          container.zoom.url &&
          thumbnail['zoom'] &&
          thumbnail['zoom'].url &&
          container.zoom.url === thumbnail['zoom'].url
        );
      }),
    );
  }

  /**
   * Return an array of CarouselItems for the product thumbnails.
   * In case there are less then 2 thumbs, we return null.
   */
  private createThumbs(product: BOProduct): Observable<any>[] {
    if (
      !product.images ||
      !product.images.GALLERY ||
      product.images.GALLERY.length < 2
    ) {
      return [];
    }

    return (<any[]>product.images.GALLERY).map((c) => of({ container: c }));
  }
}
